import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { subscribePooled } from '../utils/realtimePoolManager';

/**
 * Subscribes to the `notifications` table for the current user via Supabase Realtime.
 * Uses the realtimePoolManager for debounced batch updates and connection pooling
 * to prevent performance degradation under high-frequency changes.
 *
 * Returns:
 *  - unreadCount   : number of unread notifications
 *  - toasts        : array of pending toast objects
 *  - dismissToast  : fn(id) to remove a toast
 *  - refreshCount  : fn() to manually re-fetch the unread count
 *  - addManualToast: fn(toast) to push a toast manually (for local events)
 */
const useRealtimeNotifications = (user) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [toasts, setToasts] = useState([]);
  const timerRefs = useRef({});
  const fetchUnreadCountRef = useRef(null);
  const addToastRef = useRef(null);

  // ─── helpers ────────────────────────────────────────────────────────────────

  const fetchUnreadCount = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { count } = await supabase
        ?.from('notifications')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('recipient_id', user?.id)
        ?.eq('is_read', false);
      setUnreadCount(count ?? 0);
    } catch (_) {
      // fail silently
    }
  }, [user?.id]);

  const TYPE_META = {
    appointment_confirmation: {
      title: 'Rendez-vous confirmé',
      icon: 'CalendarCheck',
      actionLabel: 'Voir le RDV',
      actionPath: '/appointments',
    },
    appointment: {
      title: 'Nouveau rendez-vous',
      icon: 'Calendar',
      actionLabel: 'Gérer',
      actionPath: '/appointments',
    },
    property_alert: {
      title: 'Nouvelle propriété disponible',
      icon: 'BellRing',
      actionLabel: 'Voir les biens',
      actionPath: '/property-search-listings',
    },
    property_inquiry: {
      title: 'Demande reçue',
      icon: 'Home',
      actionLabel: 'Voir la propriété',
      actionPath: '/property-search-listings',
    },
    property_update: {
      title: 'Propriété mise à jour',
      icon: 'RefreshCw',
      actionLabel: 'Voir',
      actionPath: '/property-search-listings',
    },
    booking_update: {
      title: 'Réservation mise à jour',
      icon: 'BookOpen',
      actionLabel: 'Voir la réservation',
      actionPath: '/appointments',
    },
    notary_appointment: {
      title: 'RDV Notaire',
      icon: 'Scale',
      actionLabel: 'Voir le RDV',
      actionPath: '/notary-booking',
    },
    document_ready: {
      title: 'Document disponible',
      icon: 'FileText',
      actionLabel: 'Télécharger',
      actionPath: '/account-dashboard',
    },
    general: {
      title: 'Notification',
      icon: 'Bell',
      actionLabel: 'Voir',
      actionPath: '/notifications',
    },
    security_alert: {
      title: 'Alerte de sécurité',
      icon: 'ShieldAlert',
      actionLabel: 'Voir la sécurité',
      actionPath: '/user-profile',
    },
  };

  const addToast = useCallback((notification) => {
    const type = notification?.type || 'general';
    const meta = TYPE_META?.[type] || TYPE_META?.general;

    const toast = {
      id: notification?.id || `toast-${Date.now()}-${Math.random()}`,
      type,
      title: notification?.title || meta.title,
      icon: notification?.icon || meta.icon,
      message: notification?.message || notification?.body || '',
      actionLabel: notification?.action_label || meta.actionLabel,
      actionPath: notification?.action_url || notification?.action_path || meta.actionPath,
    };

    setToasts((prev) => {
      const updated = [...prev, toast];
      return updated?.slice(-4);
    });

    // Auto-dismiss after 6 s
    const timer = setTimeout(() => {
      setToasts((prev) => prev?.filter((t) => t?.id !== toast?.id));
      delete timerRefs?.current?.[toast?.id];
    }, 6000);

    timerRefs.current[toast.id] = timer;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep refs in sync so the subscription closure always calls the latest version
  useEffect(() => {
    fetchUnreadCountRef.current = fetchUnreadCount;
  }, [fetchUnreadCount]);

  useEffect(() => {
    addToastRef.current = addToast;
  }, [addToast]);

  const dismissToast = useCallback((id) => {
    if (timerRefs?.current?.[id]) {
      clearTimeout(timerRefs?.current?.[id]);
      delete timerRefs?.current?.[id];
    }
    setToasts((prev) => prev?.filter((t) => t?.id !== id));
  }, []);

  /**
   * Manually push a toast — useful for local confirmation events
   */
  const addManualToast = useCallback((toast) => {
    addToast({ id: `manual-${Date.now()}`, ...toast });
  }, [addToast]);

  // ─── pooled real-time subscription ──────────────────────────────────────────

  useEffect(() => {
    if (!user?.id) return;

    // Initial count
    fetchUnreadCountRef?.current?.();

    // Use a stable channel name per user — the pool manager handles deduplication
    const channelName = `realtime:notifications:${user?.id}`;

    const unsubscribe = subscribePooled({
      channelName,
      listeners: [
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${user?.id}`,
        },
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${user?.id}`,
        },
      ],
      // debounce: coalesce rapid bursts into a single batch flush (300 ms)
      debounceMs: 300,
      callback: (batch) => {
        // Process the batch: separate INSERTs (show toast) from UPDATEs (refresh count)
        let hasUpdate = false;
        batch?.forEach((payload) => {
          if (payload?.eventType === 'INSERT') {
            setUnreadCount((c) => c + 1);
            addToastRef?.current?.(payload?.new);
          } else {
            hasUpdate = true;
          }
        });
        if (hasUpdate) {
          fetchUnreadCountRef?.current?.();
        }
      },
    });

    return () => {
      // Clear all pending toast timers
      Object.values(timerRefs?.current ?? {})?.forEach(clearTimeout);
      timerRefs.current = {};
      unsubscribe();
    };
  }, [user?.id]); // Only re-subscribe when user ID changes

  return { unreadCount, toasts, dismissToast, refreshCount: fetchUnreadCount, addManualToast };
};

export default useRealtimeNotifications;
